import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { ErrorBanner } from "rhombus-react";
import { ADTYPE_LABELS } from "social/constants";
import { CTAS_BY_ADTYPE } from "social/constants/CTA";
import { titleCase } from "utils/common";
import { PLATFORM_BY_ADTYPE, SOCIAL_AD_TYPES } from "utils/constants";
import { getErrorMessagesByAdType } from "selectors/Social2";

export function adTypeSort(typeA, typeB){
	if(typeB.platform !== typeA.platform){
		return typeA.platform.localeCompare(typeB.platform);
	} else {
		return typeA.adType.localeCompare(typeB.adType);
	}
}

export function normalizeCardAPIWarnings(warnings = [], cards = []){
	/*
		warnings is expected to be an array of objects in the form:
		```
		[
			{
				// Item has no `id` property if it's a carousel-level problem
				// i.e. not card-specific, but related to the carousel's cards
				size: [
					"Carousel requires at least 2 cards"
				]
			},
			{
				id: 2650 // The card ID
				image: [
					"Image is required."
				],
				landingPage: [
					"Invalid format for landing page. URL format is required.",
					"Max landing page length is 800 characters."
				],
			},
			...
		]
		```
		`cards` is the `socialValues.cards` data from the Creative, which we need to be able
		to reference the card IDs and get their order, which we'll use as an index.

		We want to return an object in the form:
		{
			cards: {
				"0": { // `id` is removed, and this index is the `order` for the card with the matching id
					image: [
						"Image is required.",
					],
					landingPage: [
						"Invalid format for landing page. URL format is required.",
						"Max landing page length is 800 characters.",
					]
				},
			},
			"Cards size": [
				"Carousel requires at least 2 cards",
			],
		}
	*/
	if(warnings.length){
		const extraWarnings = {};
		const normalized = warnings.reduce((prev, cardWarnings) => {
			const { id } = cardWarnings;

			if(!id){
				// This is warnings for the whole carousel, rather than a specific card
				Object.entries(cardWarnings).forEach(([key, value]) => {
					extraWarnings[`Cards ${key}`] = value;
				});
			} else {
				const order = cards.find(card => card.id === id)?.order;
				if(order !== undefined){ // We test for undefined because order might be 0 (i.e. Card 1)
					const idStripped = { ...cardWarnings };
					delete idStripped.id;
					prev[order] = idStripped;
				}
			}

			return prev;
		}, {});

		return {
			...extraWarnings,
			cards: normalized,
		};
	}
}

export function adTypePrettyName(adType) {
	return `${PLATFORM_BY_ADTYPE[adType]} ${ADTYPE_LABELS[adType]}`;
}

export function prettifyCardWarnings(warnings){
	/*
		`warnings` comes in as an object, with numbered keys corresponding to the `card.order`.
		It's basically an array, but you can't use Array methods on it, and it may be sparsely populated.

		e.g.
			{
				"0": {
					"landingPage": "Field is required"
				},
				"2": {
					"image": "Field is required"
				}
			}

		We convert this to an array of strings like:
		"Card 1 - Landing Page - Field is required" // Cards are indexed by 0 internally, but named starting at 1.
		"Card 3 - Image - Field is required"
	*/
	const messages = [];
	Object.entries(warnings).map(([cardOrder, cardWarnings]) => {
		Object.entries(cardWarnings).forEach(([field, fieldWarning]) => {
			/*
				fieldWarning may be a string (when it comes from redux-form validation),
				or an array-like object (when it comes from the API), so we just normalize to an array of strings
			*/
			let warningStrings = (typeof(fieldWarning) === "object") ? Object.values(fieldWarning) : [fieldWarning];
			warningStrings.forEach((warning) => {
				messages.push(`Card ${Number(cardOrder)+1} - ${titleCase(field)} - ${warning}`);
			});
		});
	});

	return messages;
}

export function prettifyMessages(messageMap) {
	return Object.keys(messageMap).reduce((prev, key) => {
		const messageData = messageMap[key];

		if(key === "cards"){
			prev.push(...prettifyCardWarnings(messageData));
			return prev;
		}

		const label = titleCase(key);
		const prefix = `${label} - `;

		// For API errors, we will receive an array of messages and we want to show them all.
		if (Array.isArray(messageData)) {
			messageData.forEach((mes) => {
				prev.push(`${prefix}${mes}`);
			});
		} else if (typeof messageData === "object") { // Redux converts some arrays into objects with integer keys
			Object.values(messageData).forEach((mes) => {
				prev.push(`${prefix}${mes}`);
			});
		} else {
			prev.push(`${prefix}${messageData}`);
		}

		return prev;
	}, []);
}

export function formatAPIOption(option){
	/*
		When we get Creatives from the API, anything that comes from a Select will usually be
		in the form { id, name } instead of { value, label }, which we use with Select inputs.

		This function will format the API value to fill in `value` and `label` with what the
		UI expects, if they're missing, and will also fallback to using the `id` property for both
		if nothing better is available.
	*/

	return {
		...option,
		label: option?.label || option?.name || option?.username || option?.id,
		value: option?.value || option?.id,
	};
}

export function normalizeAPIOption(option){
	/*
		Our form inputs use options in { value, label } format, but the API generally expects
		them as { id, name }, so we store them with those properties for easier serialization.

		In the event that we do not have an option, we return null to indicate that nothing was set.
	*/

	if (!option) {
		return null;
	}

	return {
		...option,
		id: option.value,
		name: option.label,
	};
}

export function mapStringToOption(value){
	return {
		value,
		label: value,
	};
}

export function normalizeOptionToString(option){
	return option?.value || null;
}

export function mapFacebookStringToOption(value) {
	return {
		value,
		id: value,
		label: value,
	};
}

export function dynamicMapStringToOption(value){
	return {
		...value,
		value: mapStringToOption(value?.value),
	};
}

export function dynamicNormalizeOptionToString(option){
	return {
		...option,
		value: normalizeOptionToString(option?.value),
	};
}

/**
 * Some fields (checkbox, SponsorPageId) on Creatives are treated as dynamic, but aren't
 * We can normalize and format the values so that redux-form saves things correctly without us needing complicated maps
 */
export function nonDynamicFields(){
	return {
		format(value){
			return value?.value;
		},
		normalize(value){
			return {
				isDynamic:false,
				dynVarName:"",
				value,
			};
		},
	};
}

/*
	CTA Utils
*/
export function intersection(lists){
	/*
		This computes the Set intersection of an array of arrays of items.
	*/
	if(lists.length === 0){
		return [];
	}
	if(lists.length === 1){
		return lists[0];
	}

	let [listA, listB, ...rest] = lists;

	if(lists.length === 2){ // eslint-disable-line no-magic-numbers
		/*
			Iterate over the shorter list, as it will be a strict superset of the intersection
		*/
		let [shortest, longestSet] = (listA.length < listB.length) ? [listA, new Set(listB)] : [listB, new Set(listA)];
		let intersectionSet = new Set();

		for(let i=0; i<shortest.length; i++){
			let item = shortest[i];
			if(longestSet.has(item)){
				intersectionSet.add(item);
			}
		}

		return [...intersectionSet];
	}

	/*
		If there are more than 2 lists, compute the intersection of the first two,
		and recursively compute that list against the rest
	*/
	return intersection([intersection([listA, listB]), ...rest]);
}

export function getSharedCTAs(adTypes, extraCTASByAdType = {}){
	/*
		This returns the Set intersection of the corresponding CTA lists for each adType,
		as option objects to be used in the Shared Content CTA dropdown.

		The function will preferentially use our static hash of values,
		but you can pass in extraCTASByAdType, which will be referenced as a backup.

		It should be an object in the form:
		{
			[SOCIAL_AD_TYPES.WHATEVER_TYPE]: ["CTA1", "CTA2", ...],
			...
		}

		This allows for dynamic lists of CTAs from an API, etc.
	*/
	let listSet = adTypes.reduce((prev, adType) => {
		// Some adTypes will have the same CTA list reference, so we use a Set to minimize what we have to iterate over
		let ctas = CTAS_BY_ADTYPE[adType] || extraCTASByAdType[adType];
		if(ctas){
			prev.add(ctas);
		}
		return prev;
	}, new Set());

	let values = intersection([...listSet]);
	return values.map(mapStringToOption);
}

export function useErrorBanner(adType, showPristineErrors, fieldConfig){
	const errorMessages = useSelector(getErrorMessagesByAdType(adType, showPristineErrors));
	const prettyErrorMessages = useMemo(() => (
		prettifyMessages(errorMessages, fieldConfig)
	), [errorMessages]);
	const hasErrors = prettyErrorMessages.length;

	if (hasErrors) {
		return <ErrorBanner title="Sorry, please fix the errors below" messages={prettyErrorMessages} />;
	}

	return null;
}

export function getCardIndexFromName(name){
	/*
		This takes a form field name like "facebookCarouselMO.cards[0].landingPage", and
		extracts a 1-based card index, or "N" if no index is found.

		We need this for generating default dynamic variable names like "card1_clickTag1"
	*/
	const zeroIndexMatch = name.match(/cards\[(\d+)\]/)?.[1];
	if(zeroIndexMatch){
		return `${Number(zeroIndexMatch) + 1}`;
	}
	return "N";
}


export function getPlacementPrefix(fieldName){
	/*
		Placement field names are formatted like "facebookImageMO.placements.Square.image",
		while the default field names are formatted like "facebookImageMO.image".

		If the name is formatted like a placement, we extract the placement name and return it
		lowercased, with an underscore.  Otherwise, we return an empty string.
	*/
	const namePattern = /placements\.(\w+)\./;
	const matches = fieldName.match(namePattern);
	return matches ? `${matches[1]}_`.toLowerCase() : "";
}

export function nameWithPlacementPrefix(defaultName){
	/*
		This returns a function that can be given to a Field for defaultDynVarName to generate
		a name with an appropriate placement prefix if needed.

		If the corresponding Creative field has a dynVarName set, its value will be used for the
		suffix, otherwise the defaultName will be used.

		e.g. "square_image" for a Square placement, or just "image" for the Default placement.
	*/
	return ({ name }, creativeValues = {}) => {
		const prefix = getPlacementPrefix(name);

		const nameTokens = name.split(".");
		const fieldName = nameTokens[nameTokens.length - 1];

		const defaultPlacementValue = creativeValues[fieldName]?.dynVarName;

		const suffix = defaultPlacementValue || defaultName;

		return `${prefix}${suffix}`;
	};
}

export function getFlatSocialVals(socialValues = {}) {
	let flatSocVals = {};
	Object.entries(socialValues).forEach((v) => {
		const [key, value] = v;
		if(/cards/i.test(key)){
			flatSocVals[key] = value?.map((card) => {
				return getFlatSocialVals(card);
			});
		} else {
			if(/image|video/i.test(key)) {
				flatSocVals[key] = value;
			} else {
				flatSocVals[key] = value?.hasOwnProperty("value") ? value?.value : value;
			}
		}
	});
	return flatSocVals;
}

const nonObjCtaTypes = [
	SOCIAL_AD_TYPES.LINKEDIN_VIDEO,
	SOCIAL_AD_TYPES.LINKEDIN_CAROUSEL,
	SOCIAL_AD_TYPES.SNAPCHAT_IMAGE,
	SOCIAL_AD_TYPES.SNAPCHAT_VIDEO,
	SOCIAL_AD_TYPES.TIKTOK_VIDEO,
];

export function getSocialValuesFromVariables(socialValues = {}, variables = {}, libraryId, adType){
	let currentSocial = {};
	Object.entries(socialValues).forEach((entries) => {
		let [key, value] = entries
			, currentVar = null
			;

		if(value === null){
			return;
		}

		if(key === "cards"){
			currentSocial[key] = value.map((card) => {
				return {
					...getSocialValuesFromVariables(card, variables, libraryId),
					//type and order are necessary for carousel previews
					type: card.type,
					order: card.order,
					id: card.id,
				};
			});
			return;
		}

		if(key === "placements") {
			/** This map needs to be separate from 'cards' map because we
			 * need to spread the whole object, which breaks carousels
			 */
			currentSocial[key] = value.map((placement) => {
				return {
					...getSocialValuesFromVariables(placement, variables, libraryId),
					...placement,
				};
			});
			return;
		}

		if(value?.dynVarName){
			currentVar = variables[value.dynVarName];
		}

		if(!value.isDynamic){
			if(/image|video|thumb/i.test(key)){
				currentSocial[key] = {
					...value,
				};
			} else {
				currentSocial[key] = value.value;
			}
		} else if(currentVar){
			if(/text/i.test(currentVar.type)){
				currentSocial[key] = currentVar.data;

				//Not supported right now, but might as well add it for future
				if(currentVar.assetId && !currentVar.isFormatted){
					currentSocial[key] = currentVar.prop;
				}

				if(/cta/i.test(key)) {
					/** any adType ouside this array expects
					 * an object with cta.name for rendering the
					 * adType
					 */
					if(!nonObjCtaTypes.includes(adType)) {
						currentSocial[key] = {
							...currentVar,
							name: currentVar.data,
						};
					}
				}
			}
			if(/image/i.test(currentVar.type)){
				let imgSrc;
				if(currentVar.prop) {
					//redux form value
					let [, src] = currentVar.prop.split(".net");
					imgSrc = src;
				} else {
					imgSrc = `/crm/v1/asset-manager/${libraryId}/images/instantAssets/${currentVar.data}`;
				}
				currentSocial[key] = {
					id:currentVar.assetId,
					name:currentVar.name,
					src: imgSrc,
				};
			}

			if(/video/i.test(currentVar.type)){
				currentSocial[key] = {
					id:currentVar.assetId,
					name:currentVar.name,
					src:`/crm/v1/asset-manager/${libraryId}/videos/${currentVar.data}`,
				};
			}
		} else {
			if(value?.value) {
				currentSocial[key] = value.value;
			}
		}
	});

	return {
		...currentSocial,
		placementAssetCustomizationType: socialValues?.placementAssetCustomizationType,
	};
}


export const makeAdstudioSimpleAssetUrl = (creative, libraryId, versionId, fileExt, assetType) => {
	if(assetType === "image") {
		return `/crm/v1/asset-manager/${libraryId}/${creative.id}/adstudio-render-${creative.id}-${versionId}.${fileExt}`;
	}
	return `/crm/v1/asset-manager/${libraryId}/adstudio-render-${creative.id}-${versionId}.${fileExt}`;
};

export const makeAdstudioPacAssetUrl = (creative, libraryId, pacTypeId, versionId, fileExt, assetType) => {
	if(assetType === "image") {
		return `/crm/v1/asset-manager/${libraryId}/${creative.id}/adstudio-render-${creative.id}-${pacTypeId}-${versionId}.${fileExt}`;
	}
	return `/crm/v1/asset-manager/${libraryId}/adstudio-render-${creative.id}-${pacTypeId}-${versionId}.${fileExt}`;
};

export const makeAdstudioCarouselAssetUrl = (creative, libraryId, card, versionId, fileExt, assetType) => {
	if(assetType === "image") {
		`/crm/v1/asset-manager/${libraryId}/${creative.id}/adstudio-render-${creative.id}-${card.id}-${versionId}.${fileExt}`;
	}
	return `/crm/v1/asset-manager/${libraryId}/adstudio-render-${creative.id}-${card.id}-${versionId}.${fileExt}`;
};
